import { Component, DestroyRef, Inject, OnInit, Optional } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { provideTranslocoScope, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppMaterialModule } from '../../../app-material.module';
import { ConfirmDialogInputComponent } from '../../../shared/components/confirm-dialog-input/confirm-dialog-input.component';
import { openBlockedFeatureDialog } from '../../../shared/components/dialogs/dialog-block/dialog-block.helper';
import { AuthService } from '../../../shared/services/auth.service';
import { FontesTrafegoService } from '../../../shared/services/fontes-trafego/fontes-trafego.service';
import { UsageLimitService } from '../../../shared/services/usage-limit/usage-limit.service';
import { ApiResult } from '../../../shared/models/api-result.models';
import { FeatureEnum } from '../../../shared/enum/feature.enum';
import { REDE_ADS_CATALOG, RedeAdsCatalogEntry } from '../../../shared/rede-ads/rede-ads.registry';
import {
    changeCopy,
    listPendingChanges,
    PendingRedeAdsChanges,
    readSaveError,
    resourceKeys,
    sameIds,
    selectionFromCliente,
} from './fontes-trafego-page.utils';

export const FORCED_REDE_ADS_DIALOG_ID = 'forced-rede-ads-selection';

@Component({
    selector: 'app-fontes-trafego-page',
    standalone: true,
    imports: [AppMaterialModule, TranslocoPipe],
    providers: [...provideTranslocoScope('fontesTrafego')],
    templateUrl: './fontes-trafego-page.component.html',
    styleUrl: './fontes-trafego-page.component.scss',
})
export class FontesTrafegoPageComponent implements OnInit {
    readonly networks = REDE_ADS_CATALOG;

    selectedIds = new Set<number>();
    totalDisponivel = 0;
    isSaving = false;
    readonly isForcedSelection: boolean;

    private savedIds = new Set<number>();

    constructor(
        private authService: AuthService,
        private fontesTrafegoService: FontesTrafegoService,
        private usageLimitService: UsageLimitService,
        private toastrService: ToastrService,
        private translocoService: TranslocoService,
        private dialog: MatDialog,
        private destroyRef: DestroyRef,
        @Optional() private dialogRef: MatDialogRef<FontesTrafegoPageComponent> | null,
        @Optional() @Inject(MAT_DIALOG_DATA) dialogData: { forceSelection?: boolean } | null,
    ) {
        this.isForcedSelection = dialogData?.forceSelection === true;
    }

    ngOnInit(): void {
        this.fontesTrafegoService.redeAdsAtivasUpdated$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.applyClienteState());
        this.authService.ensureProfileHydrated().subscribe(() => this.applyClienteState());
    }

    get hasPendingChanges(): boolean {
        return !sameIds(this.selectedIds, this.savedIds);
    }

    isSelected(id: number): boolean {
        return this.selectedIds.has(id);
    }

    onToggle(network: RedeAdsCatalogEntry, event: MatSlideToggleChange): void {
        const wantActive = event.checked;
        if (this.isSaving || this.isSelected(network.id) === wantActive) {
            event.source.checked = this.isSelected(network.id);
            return;
        }

        if (wantActive && !this.canActivate()) {
            event.source.checked = this.isSelected(network.id);
            return;
        }

        const nextIds = new Set(this.selectedIds);
        if (wantActive) {
            nextIds.add(network.id);
        } else {
            nextIds.delete(network.id);
        }
        this.selectedIds = nextIds;
    }

    confirmSelection(): void {
        if (!this.canShowConfirm || this.isSaving) {
            return;
        }

        this.dialog
            .open(ConfirmDialogInputComponent, {
                data: {
                    titulo: this.translate('fontesTrafego.confirm.title'),
                    descricao: this.confirmDescription(),
                },
                maxWidth: '480px',
                autoFocus: false,
            })
            .afterClosed()
            .subscribe((confirmed) => {
                if (confirmed === true) {
                    this.saveSelection();
                }
            });
    }

    get canShowConfirm(): boolean {
        if (!this.hasPendingChanges) {
            return false;
        }

        if (this.totalDisponivel > 0 && this.selectedIds.size > this.totalDisponivel) {
            return false;
        }

        return !this.isForcedSelection || this.selectedIds.size > 0;
    }

    private applyClienteState(): void {
        this.loadLimitFromCliente();
        if (this.hasPendingChanges && !this.isSaving) {
            return;
        }

        const ids = selectionFromCliente(
            this.authService.cliente?.rede_ads_ativas,
            this.networks,
            this.isForcedSelection || this.totalDisponivel > 0,
        );
        this.selectedIds = ids;
        this.savedIds = new Set(ids);
    }

    private loadLimitFromCliente(): void {
        const limit = this.usageLimitService.getLimit(FeatureEnum.TOTAL_REDE_ADS);
        const monthly = limit?.limite_mensal;
        this.totalDisponivel =
            limit != null && limit.ilimitado !== true && typeof monthly === 'number' && monthly > 0 ? monthly : 0;
    }

    private canActivate(): boolean {
        if (this.totalDisponivel <= 0 || this.selectedIds.size < this.totalDisponivel) {
            return true;
        }

        const limit = this.usageLimitService.getLimit(FeatureEnum.TOTAL_REDE_ADS);
        openBlockedFeatureDialog(
            this.dialog,
            limit
                ? {
                      ...limit,
                      limite_mensal: this.totalDisponivel,
                      consumo_atual: this.selectedIds.size,
                      disponivel: 0,
                  }
                : null,
        );
        return false;
    }

    private confirmDescription(): string {
        const changes = this.pendingChanges();
        const changed = [...changes.activated, ...changes.deactivated];
        const copy = changeCopy(changes, this.translate('fontesTrafego.confirm.listConjunction'));
        const lead = this.translate(copy.leadKey, copy.params);
        const items = resourceKeys(changed)
            .map((key) => `<li>${this.translate(key)}</li>`)
            .join('');
        const question = this.translate('fontesTrafego.confirm.question');

        return `${lead}<ul>${items}</ul>${question}`;
    }

    private saveSelection(): void {
        const payload = Array.from(this.selectedIds).sort((left, right) => left - right);
        const changes = this.pendingChanges();
        const usageDelta = payload.length - this.savedIds.size;
        this.isSaving = true;

        this.fontesTrafegoService
            .updateRedeAdsAtivas(payload)
            .pipe(finalize(() => (this.isSaving = false)))
            .subscribe({
                next: (result) => this.onSaveSuccess(result, payload, changes, usageDelta),
                error: (error: HttpErrorResponse) => this.showSaveError(readSaveError(error)),
            });
    }

    private onSaveSuccess(
        result: ApiResult,
        payload: number[],
        changes: PendingRedeAdsChanges,
        usageDelta: number,
    ): void {
        if (!result?.success) {
            this.showSaveError(result?.message);
            return;
        }

        this.applyUsageDelta(usageDelta);
        if (this.authService.cliente) {
            this.authService.cliente.rede_ads_ativas = payload;
        }
        this.fontesTrafegoService.redeAdsAtivasUpdated$.next();

        const copy = changeCopy(changes, this.translate('fontesTrafego.confirm.listConjunction'));
        this.toastrService.success(this.translate(copy.toastKey, copy.params));
        if (this.isForcedSelection) {
            this.dialogRef?.close(true);
        }
    }

    private applyUsageDelta(delta: number): void {
        if (delta === 0) {
            return;
        }

        const step: 1 | -1 = delta > 0 ? 1 : -1;
        for (let index = 0; index < Math.abs(delta); index++) {
            this.usageLimitService.applyLocalUsage(FeatureEnum.TOTAL_REDE_ADS, step);
        }
    }

    private pendingChanges(): PendingRedeAdsChanges {
        return listPendingChanges(this.networks, this.selectedIds, this.savedIds);
    }

    private showSaveError(message?: string): void {
        this.toastrService.error(message || this.translate('fontesTrafego.toasts.savedError'));
    }

    private translate(key: string, params?: Record<string, string>): string {
        return this.translocoService.translate(key, params);
    }
}
